import { useState, type ReactNode } from 'react';
import {
  IconCertificate,
  IconClipboardCheck,
  IconDownload,
  IconFileDescription,
  IconFileInvoice,
  IconFileText,
  IconLock,
  IconReportAnalytics,
  IconShieldCheck,
} from '@tabler/icons-react';
import {
  Badge,
  IconButton,
  List,
  Panel,
  SegmentedControl,
  formatDate,
  useToast,
  type ListItemData,
} from 'escrowbuild-ui';

import {
  usePublishedDocuments,
  useReports,
  useT,
  type AppTranslationKey,
} from '@/i18n';
import {
  PROJECTS,
  participationsForInvestor,
  type DocumentRecord,
  type ReportRecord,
} from '@/data/domain';

/* =============================================================================
   Investor portal — authorised documents and reports
   -----------------------------------------------------------------------------
   The filing cabinet, not the working file store. `publishedDocuments` is the
   only door into it: a document that is still under review, or one that was
   rejected, is absent here even though it exists in the platform and even though
   its project is in this portfolio.

   The final report is presented on its own because §16 makes it a different kind
   of object — issued once, after the sale, as the complete account of the
   development rather than a period of it.
   ============================================================================= */

/** The signed-in investor — see the note in `PortalHomePage`. */
const INVESTOR_ID = 'prado';

const ALL = 'all';

/** The word each document kind is shown as. The code itself is a wire value. */
const DOCUMENT_KIND: Record<DocumentRecord['type'], AppTranslationKey> = {
  invoice: 'status.documentType.invoice',
  receipt: 'status.documentType.receipt',
  contract: 'status.documentType.contract',
  permit: 'status.documentType.permit',
  quote: 'status.documentType.quote',
  report: 'status.documentType.report',
  inspection: 'status.documentType.inspection',
};

const DOCUMENT_ICONS: Record<DocumentRecord['type'], ReactNode> = {
  invoice: <IconFileInvoice size={18} aria-hidden="true" />,
  receipt: <IconFileText size={18} aria-hidden="true" />,
  contract: <IconClipboardCheck size={18} aria-hidden="true" />,
  permit: <IconCertificate size={18} aria-hidden="true" />,
  quote: <IconFileDescription size={18} aria-hidden="true" />,
  report: <IconReportAnalytics size={18} aria-hidden="true" />,
  inspection: <IconClipboardCheck size={18} aria-hidden="true" />,
};

const REPORT_SCOPES: Record<ReportRecord['kind'], AppTranslationKey> = {
  period: 'portal.documents.reportScope.period',
  phase: 'portal.documents.reportScope.phase',
  final: 'portal.documents.reportScope.final',
};

export function PortalDocumentsPage() {
  const [scope, setScope] = useState<string>(ALL);
  const { toast } = useToast();
  const t = useT();

  /* The filing cabinet, in the reader's language: a document's name and a
     report's title and period are the records' own words. Both lists are read
     whole and narrowed per development below, because a development's documents
     are needed inside a loop and a hook cannot run there. */
  const released = usePublishedDocuments();
  const register = useReports();

  const projectIds = new Set(participationsForInvestor(INVESTOR_ID).map((entry) => entry.projectId));
  const projects = PROJECTS.filter((project) => projectIds.has(project.id));
  const scoped = scope === ALL ? projects : projects.filter((project) => project.id === scope);

  /*
     There is no file to serve from a static build, so the affordance says what
     happened rather than simulating a download that never starts.
  */
  function requestDownload(name: string) {
    toast({
      variant: 'info',
      title: t('portal.documents.downloadTitle'),
      description: t('portal.documents.downloadBody', { name }),
    });
  }

  const reports = register
    .filter((report) => report.published && projectIds.has(report.projectId))
    .filter((report) => scope === ALL || report.projectId === scope);

  const documentCount = scoped.reduce(
    (sum, project) => sum + released.filter((entry) => entry.projectId === project.id).length,
    0,
  );

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <p className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
          {t('shell.portal.tagline')}
        </p>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('portal.documents.title')}
        </h1>
        <p className="max-w-2xl text-sm text-body">{t('portal.documents.intro')}</p>
      </header>

      <div className="flex flex-col gap-4">
        <SegmentedControl
          label={t('portal.documents.filterLabel')}
          value={scope}
          onValueChange={setScope}
          options={[
            { value: ALL, label: t('portal.documents.bothDevelopments') },
            ...projects.map((project) => ({ value: project.id, label: project.name })),
          ]}
        />
        <p role="status" className="text-xs text-muted">
          {t('portal.documents.count', { count: documentCount })} ·{' '}
          {t('portal.documents.reportCount', { count: reports.length })}.
        </p>
      </div>

      {scoped.map((project) => {
        const documents = released.filter((entry) => entry.projectId === project.id);

        const items: ListItemData[] = documents.map((document) => ({
          id: document.id,
          icon: DOCUMENT_ICONS[document.type],
          title: document.name,
          subtitle: `${t(DOCUMENT_KIND[document.type])} · ${formatDate(document.date)} · ${document.size}`,
          trailing: (
            <IconButton
              label={t('portal.documents.downloadLabel', { name: document.name })}
              icon={<IconDownload size={16} />}
              variant="ghost"
              size="sm"
              onClick={() => requestDownload(document.name)}
            />
          ),
        }));

        return (
          <Panel
            key={project.id}
            title={project.name}
            description={t('portal.documents.perProject', { count: documents.length })}
          >
            <List
              label={t('portal.documents.listLabel', { project: project.name })}
              items={items}
              divided
              emptyLabel={t('portal.documents.emptyProject')}
            />
          </Panel>
        );
      })}

      <Panel
        title={t('portal.documents.reportsTitle')}
        description={t('portal.documents.reportsDescription')}
      >
        {reports.length === 0 ? (
          <p className="text-sm text-muted">{t('portal.documents.noReports')}</p>
        ) : (
          <ul className="grid list-none gap-4 p-0 sm:grid-cols-2 xl:grid-cols-3">
            {reports.map((report) => (
              <li
                key={report.id}
                className="flex flex-col gap-3 rounded-xs border border-line bg-surface p-4 shadow-hard-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <Badge tone="info" variant="subtle" size="sm">
                    {t(REPORT_SCOPES[report.kind])}
                  </Badge>
                  <IconButton
                    label={t('portal.documents.downloadLabel', { name: report.title })}
                    icon={<IconDownload size={16} />}
                    variant="ghost"
                    size="sm"
                    onClick={() => requestDownload(report.title)}
                  />
                </div>
                <h3 className="font-display text-sm font-semibold uppercase tracking-title text-heading">
                  {report.title}
                </h3>
                <dl className="mt-auto flex flex-wrap gap-x-6 gap-y-2 text-xs">
                  <div>
                    <dt className="uppercase tracking-label text-muted">
                      {t('portal.documents.period')}
                    </dt>
                    <dd className="mt-0.5 text-body">{report.period}</dd>
                  </div>
                  <div>
                    <dt className="uppercase tracking-label text-muted">
                      {t('portal.documents.pages')}
                    </dt>
                    <dd className="mt-0.5 text-body">{report.pages}</dd>
                  </div>
                  <div>
                    <dt className="uppercase tracking-label text-muted">
                      {t('portal.documents.generated')}
                    </dt>
                    <dd className="mt-0.5 text-body">{formatDate(report.generatedAt)}</dd>
                  </div>
                </dl>
                <p className="text-xs text-muted">
                  {t('portal.documents.preparedBy', { name: report.generatedBy })}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        variant="elevated"
        className="border-l-4 border-l-primary"
        title={t('portal.documents.finalTitle')}
        description={t('portal.documents.finalDescription')}
        headerActions={
          <Badge tone="neutral" variant="subtle" size="sm" icon={<IconLock size={12} aria-hidden="true" />}>
            {t('portal.documents.notIssued')}
          </Badge>
        }
      >
        <p className="max-w-3xl text-sm leading-relaxed text-body">
          {t('portal.documents.finalBody')}
        </p>

        <ul className="mt-5 list-none divide-y divide-line-subtle p-0">
          {scoped.map((project) => (
            <li
              key={project.id}
              className="flex flex-wrap items-baseline justify-between gap-2 py-3 first:pt-0"
            >
              <span className="inline-flex items-center gap-2 text-sm text-body">
                <IconShieldCheck size={16} aria-hidden="true" className="shrink-0 text-muted" />
                {project.name}
              </span>
              <span className="text-xs text-muted">
                {t('portal.documents.releasesAfterSale', {
                  date: formatDate(project.projectedSale),
                })}
              </span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
